import ShopType from "@/enum/ShopType";
import AppVersion from "@/enum/AppVersion";
import { get, getAxiosByShopType, post } from "@/services/http/platformServer";
import { PagedResult } from "./types";
import type { FromFeature } from "../PremiumValidator";
import { AppSubscriptionOrder, OrderUpgradeRecord } from "./appSubscriptionOrder.type";

export * from "./appSubscriptionOrder.type";

/** -------------------------订购相关------------------------------- */

export interface GetAppSubscriptionOrderListParams {
    pageNo: number;
    pageSize: number;
    shopId: number;
    tradeId?: string;
}

export function getAppSubscriptionOrderList(
    params: GetAppSubscriptionOrderListParams,
): Promise<PagedResult<AppSubscriptionOrder>> {
    return get(params.shopId, "/app_subscription/order/list", params);
}

/**
 * 同步服务市场订单
 */
export function syncAppSubscriptionOrder(shopId: number) {
    return post<void>(shopId, "/app_subscription_order/sync", {
        shopId,
    });
}

export interface AppSubscriptionPlan {
    /**
     * 周期计划名称
     */
    name: string;
    /**
     * 周期计划ID
     */
    planId: number;
    /**
     * 价格
     */
    price: number;
    /**
     * 促销价格
     */
    promotionPrice: number;
    /**
     * 周期时长
     */
    time: number;
    /**
     * 周期单位
     */
    timeUnit: string;
}

export interface AppSubscriptionVersion {
    /**
     * 版本名称
     */
    name: string;
    /**
     * 版本对应的计划/周期列表
     */
    plans: AppSubscriptionPlan[];
    /**
     * 版本对应的产品ID
     */
    productId: number;
    /**
     * 版本code
     */
    version: string;
}

/** 根据店铺平台获取各版本定价列表 */
export function getPlatformSubscriptionPricing(shopType: ShopType): Promise<AppSubscriptionVersion[]> {
    const axios = getAxiosByShopType(shopType);

    return axios.get(`/app_subscription/pricing/${shopType}`);
}

interface PayParam {
    planId: number;
    productId: number;
    shopId: number;
}

interface AppSubscriptionPaymentHtmlDto {
    /**
     * 支付页面HTML
     * 用于给商家扫描支付
     */
    paymentHtml: string;
    /**
     * 订购订单ID
     */
    subscriptionOrderId: number;
}

/**
 * 向支付系统发起支付请求, 返回支付html供用户扫码支付
 */
export function payAppSubscriptionOrder(params: PayParam): Promise<AppSubscriptionPaymentHtmlDto> {
    return post(params.shopId, "/app_subscription/order/pay", params);
}

export function getAppSubscriptionOrder(shopId: number, id: number) {
    return get<AppSubscriptionOrder>(shopId, `/app_subscription/order/get/${id}`);
}

/** -------------------------升级相关------------------------------- */

interface UpgradePayParams {
    shopId: number;
    appSubscriptionOrderId: number;
    fromFeature?: FromFeature;
}

interface SubscriptionUpgradePaymentHtml {
    /**
     * 支付页面HTML
     * 用于给商家扫描支付
     */
    paymentHtml?: string;
    /**
     * 订购订单ID
     */
    subscriptionOrderId?: number;
    /**
     * 升级记录ID
     */
    upgradeRecordId?: number;
}

/** 升级支付 */
export function upgradePay(params: UpgradePayParams) {
    return post<SubscriptionUpgradePaymentHtml>(params.shopId, "/app_subscription/order_upgrade/pay", params);
}

/** 获取升级记录 */
export function getOrderUpgradeRecord(shopId: number, id: number) {
    return get<OrderUpgradeRecord>(shopId, `/app_subscription/order_upgrade/get/${id}`);
}

/**
 * 同步升级记录的支付状态
 * 仅支持同步未付款状态的升级记录
 */
export function syncUpgradePaymentStatus(shopId: number, upgradeRecordId: number) {
    return post<OrderUpgradeRecord>(shopId, "/app_subscription/order_upgrade/sync_payment_status", {
        upgradeRecordId,
    });
}

export interface OrderUpgradeDifferential {
    /**
     * 升级对应的订购订单ID
     */
    appSubscriptionOrder?: AppSubscriptionOrder;
    /**
     * 订购订单剩余时长（天）
     */
    leftDays?: number;
    /**
     * 升级差价（元）
     */
    upgradeDifferential?: number;
}

/**
 * 获取升级差价
 */
export function getUpgradeDifferential(shopId: number, upgradeToVersion: AppVersion) {
    return post<OrderUpgradeDifferential>(shopId, "/app_subscription/order_upgrade/get_upgrade_differential", {
        shopId,
        upgradeToVersion,
    });
}
